import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/images/icon48.png" alt="Football Scores" className="w-10 h-10" />
            <h1 className="text-xl font-bold text-gray-900">Football Live Scores</h1>
          </Link>
          <Link to="/">
            <Button variant="ghost">Back to Home</Button>
          </Link>
        </div>
      </header>

      {/* Privacy Policy Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
        
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          <section>
            <p className="text-gray-600 mb-4">
              <strong>Last Updated:</strong> December 2025
            </p>
            <p className="text-gray-700 leading-relaxed">
              This Privacy Policy describes how the Football Live Scores Chrome Extension ("we", "our", or "the extension") 
              handles information. We are committed to protecting your privacy and being transparent about our practices.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Information We Do NOT Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Football Live Scores extension does NOT collect, store, or transmit any personal information. Specifically:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>We do not collect your name, email address, or any personal identifiers</li>
              <li>We do not track your browsing history or activity</li>
              <li>We do not use cookies or similar tracking technologies</li>
              <li>We do not collect or store any user account information</li>
              <li>We do not share any data with third parties for advertising or marketing</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Data Usage</h2>
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold mb-2">Match Data</h3>
                <p className="leading-relaxed">
                  The extension fetches live football match data, scores, and standings from API-Football 
                  (a third-party service). This data is displayed directly in the extension popup and is not 
                  stored on our servers. The data is temporarily cached in your browser for performance purposes only.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Local Storage</h3>
                <p className="leading-relaxed">
                  The extension uses Chrome's local storage API to save your preferences, including:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Your selected theme preference (light or dark mode)</li>
                  <li>Your last selected league</li>
                </ul>
                <p className="leading-relaxed mt-2">
                  This data is stored locally on your device only and is never transmitted to any server.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">API Requests</h3>
                <p className="leading-relaxed">
                  When you use the extension, it makes requests to API-Football to retrieve match data. 
                  These requests do not include any personal information. API-Football may collect standard 
                  web request information (such as IP address) as part of their service. Please refer to 
                  <a href="https://www.api-football.com/privacy" className="text-blue-600 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                    API-Football's Privacy Policy
                  </a> for more information.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Permissions Explained</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The extension requests the following Chrome permissions:
            </p>
            <div className="space-y-3 text-gray-700">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-1">Storage Permission</h3>
                <p className="text-sm">
                  Used to save your theme preference and selected league locally on your device.
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-1">Notifications Permission</h3>
                <p className="text-sm">
                  Used to send you alerts when matches start and when goals are scored (if you enable this feature).
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-1">Network Access</h3>
                <p className="text-sm">
                  Required to fetch live match data from API-Football's servers.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This extension uses the following third-party service:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">API-Football</h3>
              <p className="text-sm text-gray-700 mb-2">
                We use API-Football to provide real-time football match data, scores, and standings. 
                When you use the extension, requests are made to their API servers.
              </p>
              <p className="text-sm text-gray-700">
                API-Football Privacy Policy: 
                <a href="https://www.api-football.com/privacy" className="text-blue-600 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                  https://www.api-football.com/privacy
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              Since we do not collect or store any personal data on our servers, there is no risk of your 
              personal information being compromised through our extension. All preferences are stored locally 
              on your device using Chrome's secure storage API.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              This extension does not knowingly collect any information from children under the age of 13. 
              The extension is designed to display football scores and does not require any personal information 
              from users of any age.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. Any changes will be posted on this page 
              with an updated "Last Updated" date. We encourage you to review this Privacy Policy periodically 
              for any changes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Since we do not collect personal data, there is no personal data to access, modify, or delete. 
              However, you can:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Uninstall the extension at any time through Chrome's extension management page</li>
              <li>Clear your local preferences by resetting the extension</li>
              <li>Disable notifications through Chrome's settings</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions or concerns about this Privacy Policy or the extension's practices, 
              please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700">
                <strong>Email:</strong> football.livescores1@gmail.com
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Chrome Web Store:</strong> Leave a review or question on our extension page
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Consent</h2>
            <p className="text-gray-700 leading-relaxed">
              By using the Football Live Scores Chrome Extension, you consent to this Privacy Policy and 
              agree to its terms.
            </p>
          </section>

          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-gray-500 text-center">
              This extension is a one-time purchase product with no backend server, user accounts, or data collection.
            </p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/">
            <Button className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-glow))]">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
